import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, FormField, Alert, Icon } from '@chikara/ui';
import { useResetPassword } from '@/features/auth/hooks/usePasswordReset';
import { useClearStaleSession } from '@/features/auth/hooks/useClearStaleSession';
import * as ROUTES from '@/shared/constants/routes';
import styles from './AdminInvitePage.module.css';
import { t } from '@/i18n';

type Step = 'WELCOME' | 'PASSWORD' | 'SUCCESS';

export default function AdminInvitePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('WELCOME');
	const [searchParams] = useSearchParams();
	const token = searchParams.get('reference') ?? '';
  useClearStaleSession();

  useEffect(() => {
    validateToken(token);
  }, [token]);


  const {
    password,
    confirmPassword,
    passwordError,
    apiError,
    isLengthValid,
    hasNumber,
    hasSpecial,
    hasUppercase,
    isFormValid,
    isTokenExpired,
    isLoading,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleSubmit,
    goToForgotPassword,
    validateToken
  } = useResetPassword(() => setStep('SUCCESS'));

  if (isTokenExpired) {
    return (
      <div className={styles.expiredContainer}>
        <Icon name="error" size="lg" color="error" />
        <h2 className={styles.expiredTitle}>{t('resetPassword.tokenExpired')}</h2>
        <p className={styles.expiredSubtitle}>{t('resetPassword.tokenExpiredSubtitle')}</p>
        <Button onClick={goToForgotPassword} className={styles.submitButton}>
          {t('resetPassword.tokenExpiredAction')}
        </Button>
      </div>
    );
  }

  const renderWelcome = () => (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('adminInvite.welcome.title')}</h2>
        <p className={styles.subtitle}>{t('adminInvite.welcome.subtitle')}</p>
      </div>
      <Button 
        onClick={() => setStep('PASSWORD')} disabled={apiError || isLoading ? true : false}
        className={styles.submitButton}
      >
        {t('adminInvite.welcome.action')}
      </Button>
    </div>
  );

  const renderPasswordSetup = () => (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('adminInvite.setupPassword.title')}</h2>
        <p className={styles.subtitle}>{t('adminInvite.setupPassword.subtitle')}</p>
      </div>

      <FormField
        label={t('resetPassword.password')}
        type="password"
        value={password}
        onChange={handlePasswordChange}
        required
      />

      <FormField
        label={t('resetPassword.confirmPassword')}
        type="password"
        value={confirmPassword}
        onChange={handleConfirmPasswordChange}
        error={passwordError}
        required
      />

      <div className={styles.requirementsContainer}>
        <p className={styles.requirementsTitle}>{t('resetPassword.requirements.title')}</p>
        <div className={styles.requirementsList}>
          <div>
            <Icon name={ isLengthValid ? "check" : "close"} size="sm" color={isLengthValid ? 'success' : 'error'} />
            <span>{t('resetPassword.requirements.length')}</span>
          </div>
          <div>
            <Icon name={ hasNumber ? "check" : "close"} size="sm" color={hasNumber ? 'success' : 'error'} />
            <span>{t('resetPassword.requirements.number')}</span>
          </div>
          <div>
            <Icon name={ hasSpecial ? "check" : "close"} size="sm" color={hasSpecial ? 'success' : 'error'} />
            <span>{t('resetPassword.requirements.special')}</span>
          </div>
          <div>
            <Icon name={ hasUppercase ? "check" : "close"} size="sm" color={hasUppercase ? 'success' : 'error'} />
            <span>{t('resetPassword.requirements.uppercase')}</span>
          </div>
        </div>
      </div>

      {apiError && (
        <Alert variant="error" icon={<Icon name="error" color="inherit" />}>
          {apiError}
        </Alert>
      )}

      <Button
        type="submit"
        disabled={!isFormValid}
        isLoading={isLoading}
        className={styles.submitButton}
      >
        {t('adminInvite.setupPassword.action')}
      </Button>
    </form>
  );

  const renderSuccess = () => (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('adminInvite.success.title')}</h2>
        <p className={styles.subtitle}>{t('adminInvite.success.subtitle')}</p>
      </div>
      <Button 
        onClick={() => navigate(ROUTES.LOGIN)} 
        className={styles.submitButton}
      >
        {t('adminInvite.success.action')}
      </Button>
    </div>
  );

  return (
    <div className={styles.container}>
      {step === 'WELCOME' && renderWelcome()}
      {step === 'PASSWORD' && renderPasswordSetup()}
      {step === 'SUCCESS' && renderSuccess()}
    </div>
  );
}

export { AdminInvitePage };
